import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { ConsultationModal } from "@/components/ConsultationModal";
import { Reveal, itemAnim, heroAnim } from "@/components/Reveal";

const IMG = {
  linen: "https://images.unsplash.com/photo-1790469927308-bf31e9227e9e?auto=format&fit=crop&w=1200&q=80",
  ac: "https://images.unsplash.com/photo-1660330589827-da8ab7dd3c02?auto=format&fit=crop&w=1200&q=80",
  pool: "https://images.unsplash.com/photo-1774109556498-652c0458d4af?auto=format&fit=crop&w=1200&q=80",
  garden: "https://images.unsplash.com/photo-1590476490365-e1a8e952fee4?auto=format&fit=crop&w=1200&q=80",
  bedroom: "https://images.unsplash.com/photo-1519710889408-a67e1c7e0452?auto=format&fit=crop&w=1400&q=85",
};

const copy = {
  en: {
    nav: ["Our approach", "Services", "Estates", "About"],
    contact: "Talk to our team",
    eyebrow: "HOUSEKEEPING & MAINTENANCE",
    heroTitle: <>Cared for on a rhythm, <em>not</em> on a reaction.</>,
    heroText: "A villa stays beautiful when it is looked after before something breaks. Our team keeps every room, pool and garden in resort condition, on a schedule you never have to think about.",
    seeList: "See what we take care of",
    servicesEyebrow: "WHAT WE TAKE CARE OF",
    services: [
      ["Periodical cleaning", "Hotel-grade cleaning after every check-out, plus scheduled deep cleans of kitchens, bathrooms, mattresses, curtains and outdoor areas. Fresh linen and amenities for every guest.", IMG.linen],
      ["Periodical maintenance", "Scheduled checks of air conditioning, plumbing, electrics, water pumps, hot water and appliances. Small issues are fixed early, before they reach a guest or become a costly repair.", IMG.ac],
      ["Periodical pest control", "Regular treatment inside and around the villa by licensed pest control partners, keeping the tropical outdoors where it belongs.", IMG.garden],
      ["Periodical pool & gardening", "Pool cleaning and water balance checks, plus garden care, trimming and leaf clearing, so the outdoor spaces always look as good as the photos.", IMG.pool],
    ],
    howEyebrow: "HOW IT WORKS",
    how: [
      ["A schedule for your villa", "We set a cleaning, maintenance, pest control and pool and garden calendar based on your villa’s size and needs."],
      ["Checked and documented", "Every visit follows a checklist and is recorded with photos."],
      ["Reported to you", "Completed work, repairs and costs appear in your monthly Eden Report. Larger repairs need your approval first."],
    ],
    whyEyebrow: "WHY IT MATTERS FOR OWNERS",
    whyTitle: <>Protect your villa. <em>Protect</em> your reviews.</>,
    whyPoints: [
      "Preventive care extends the life of your pool, AC units, furniture and finishes.",
      "Fewer guest complaints and refunds, and stronger cleanliness ratings on every platform.",
      "No chasing cleaners, gardeners or technicians. We manage the team and the schedule.",
      "Full transparency: every visit, repair and cost in one monthly report.",
    ],
    ctaTitle: <>Want your villa <br /><em>looked after properly?</em></>,
    ctaText: "Tell us about your property. We’ll suggest a care schedule that fits it.",
    footer: "Keeping Bali’s finest estates in their best light.",
    footerDist: "Distribution Channels",
  },
  id: {
    nav: ["Pendekatan kami", "Layanan", "Properti", "Tentang"],
    contact: "Hubungi tim kami",
    eyebrow: "HOUSEKEEPING & PEMELIHARAAN",
    heroTitle: <>Dirawat dengan ritme, <em>bukan</em> menunggu rusak.</>,
    heroText: "Villa tetap indah ketika dirawat sebelum ada yang rusak. Tim kami menjaga setiap kamar, kolam, dan taman dalam kondisi resort — dengan jadwal yang tidak perlu Anda pikirkan.",
    seeList: "Lihat yang kami rawat",
    servicesEyebrow: "YANG KAMI RAWAT",
    services: [
      ["Pembersihan berkala", "Pembersihan standar hotel setelah setiap check-out, plus deep clean terjadwal untuk dapur, kamar mandi, kasur, gorden, dan area luar. Linen segar dan amenities untuk setiap tamu.", IMG.linen],
      ["Pemeliharaan berkala", "Pemeriksaan terjadwal AC, plumbing, listrik, pompa air, pemanas air, dan peralatan. Masalah kecil diperbaiki lebih awal — sebelum terasa oleh tamu atau menjadi biaya besar.", IMG.ac],
      ["Pest control berkala", "Perawatan rutin di dalam dan sekitar villa oleh mitra pest control berlisensi, menjaga alam tropis tetap di tempatnya.", IMG.garden],
      ["Kolam & taman berkala", "Pembersihan kolam dan pengecekan keseimbangan air, plus perawatan taman, pemangkasan, dan pembersihan daun — area luar selalu seindah fotonya.", IMG.pool],
    ],
    howEyebrow: "CARA KERJANYA",
    how: [
      ["Jadwal untuk villa Anda", "Kami menyusun kalender pembersihan, pemeliharaan, pest control, serta kolam & taman berdasarkan ukuran dan kebutuhan villa Anda."],
      ["Dicek dan terdokumentasi", "Setiap kunjungan mengikuti checklist dan terekam dengan foto."],
      ["Dilaporkan kepada Anda", "Pekerjaan selesai, perbaikan, dan biaya muncul di Eden Report bulanan Anda. Perbaikan besar memerlukan persetujuan Anda terlebih dahulu."],
    ],
    whyEyebrow: "MENGAPA PENTING BAGI PEMILIK",
    whyTitle: <>Lindungi villa Anda. Lindungi <em>ulasan</em> Anda.</>,
    whyPoints: [
      "Perawatan preventif memperpanjang umur kolam, AC, furnitur, dan finishing Anda.",
      "Lebih sedikit keluhan dan refund tamu, serta rating kebersihan lebih kuat di setiap platform.",
      "Tanpa mengejar petugas kebersihan, tukang kebun, atau teknisi. Kami kelola tim dan jadwalnya.",
      "Transparansi penuh: setiap kunjungan, perbaikan, dan biaya dalam satu laporan bulanan.",
    ],
    ctaTitle: <>Ingin villa Anda <br /><em>dirawat dengan benar?</em></>,
    ctaText: "Ceritakan properti Anda. Kami akan menyarankan jadwal perawatan yang paling cocok.",
    footer: "Menjaga villa terbaik Bali dalam cahaya terbaiknya.",
    footerDist: "Kanal Distribusi",
  },
};

export default function Housekeeping({ lang, setLang }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [active, setActive] = useState(0);
  const t = copy[lang];

  useEffect(() => {
    document.title = "Housekeeping & Maintenance · Eden Hospitality Bali";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Periodical cleaning, preventive maintenance, pest control and pool and garden care for Bali villas, scheduled, documented and reported every month.");
  }, []);

  return <div className="site-shell">
    <Nav lang={lang} setLang={setLang} onConsult={() => setModalOpen(true)} labels={t.nav} ctaLabel={t.contact} />

    <main id="top">
      <section className="conc-hero" data-testid="housekeeping-hero">
        <motion.p className="eyebrow gold" {...heroAnim(0.2)}>{t.eyebrow}</motion.p>
        <motion.h1 data-testid="housekeeping-heading" {...heroAnim(0.35)}>{t.heroTitle}</motion.h1>
        <motion.p className="sub" {...heroAnim(0.55)}>{t.heroText}</motion.p>
        <motion.div className="conc-hero-actions" {...heroAnim(0.75)}>
          <button className="button button-terra" onClick={() => setModalOpen(true)} data-testid="housekeeping-hero-cta">{t.contact} <ArrowUpRight size={16} /></button>
          <button className="button button-outline-light" onClick={() => document.getElementById("hk-services")?.scrollIntoView({ behavior: "smooth" })} data-testid="housekeeping-services-button">{t.seeList} <ArrowUpRight size={16} /></button>
        </motion.div>
      </section>

      <section id="hk-services" data-testid="housekeeping-services">
        <div className="conc-split">
          <Reveal>
            <p className="eyebrow">{t.servicesEyebrow}</p>
            <div>
              {t.services.map(([title, text, img], i) => (
                <div className="conc-item" key={title} onMouseEnter={() => setActive(i)} data-testid={`hk-item-${i}`}>
                  <span>0{i + 1}</span>
                  <div className="conc-item-body"><strong>{title}</strong><p>{text}</p></div>
                  <ArrowUpRight size={17} />
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <figure className="conc-photo" data-testid="housekeeping-photo">
              {t.services.map(([title, , img], i) => (
                <div key={title} className={`ph ${active === i ? "active" : ""}`} style={{ backgroundImage: `url(${img})` }} data-testid={`hk-photo-${i}`} />
              ))}
              <figcaption data-testid="housekeeping-photo-caption">{t.services[active][0]}</figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      <section className="conc-how" data-testid="housekeeping-how">
        <Reveal><p className="eyebrow">{t.howEyebrow}</p></Reveal>
        <div className="conc-how-grid">
          {t.how.map(([title, text], i) => (
            <motion.div className="conc-how-col" key={title} data-testid={`hk-how-${i}`} {...itemAnim(i)}>
              <h3>{title}</h3><p>{text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="why" data-testid="housekeeping-why">
        <Reveal className="why-text">
          <p className="eyebrow">{t.whyEyebrow}</p>
          <h2>{t.whyTitle}</h2>
          <ul className="why-list">
            {t.whyPoints.map((point, i) => <li key={i} data-testid={`hk-why-point-${i}`}><Check size={15} />{point}</li>)}
          </ul>
        </Reveal>
        <Reveal className="why-photo" delay={0.15}><div className="arch-photo" style={{ backgroundImage: `url(${IMG.bedroom})` }} data-testid="housekeeping-arch-photo" /></Reveal>
      </section>

      <section className="closing section-dark" data-testid="housekeeping-cta">
        <Reveal className="closing-content">
          <p className="eyebrow gold">EDEN HOSPITALITY</p>
          <h2>{t.ctaTitle}</h2>
          <p>{t.ctaText}</p>
          <button className="button button-light" onClick={() => setModalOpen(true)} data-testid="housekeeping-closing-cta">{t.contact} <ArrowUpRight size={17} /></button>
        </Reveal>
        <div className="closing-orb" />
      </section>
    </main>

    <Footer footerText={t.footer} contactLabel={t.contact} distLabel={t.footerDist} onConsult={() => setModalOpen(true)} />
    <ConsultationModal lang={lang} open={modalOpen} onClose={() => setModalOpen(false)} ctaLabel={t.contact} />
  </div>;
}
